import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  CircularProgress,
  Box,
  Typography,
  Paper,
  FormControlLabel,
  Checkbox,
  Divider,
  Grid,
} from "@mui/material";
import SecurityIcon from "@mui/icons-material/Security";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { manageAdminAccountPermissions } from "../../../../services/Admin/AdminUsersAccountsApi";

const AddPermissionsDialog = ({ open, onClose, admin, onUpdated }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const [permissions, setPermissions] = useState({
    add: false,
    edit: false,
    delete: false,
    view: false,
    print: false,
    export: false,
  });

  const permissionsList = [
    {
      key: "view",
      label: "عرض",
      description: "السماح بعرض البيانات",
      icon: "👁️",
    },
    {
      key: "add",
      label: "إضافة",
      description: "السماح بإضافة بيانات جديدة",
      icon: "➕",
    },
    {
      key: "edit",
      label: "تعديل",
      description: "السماح بتعديل البيانات الموجودة",
      icon: "✏️",
    },
    {
      key: "delete",
      label: "حذف",
      description: "السماح بحذف البيانات",
      icon: "🗑️",
    },
    {
      key: "print",
      label: "طباعة",
      description: "السماح بطباعة التقارير",
      icon: "🖨️",
    },
    {
      key: "export",
      label: "تصدير",
      description: "السماح بتصدير البيانات",
      icon: "📤",
    },
  ];

  // تحميل الصلاحيات الحالية عند فتح الديالوج
  useEffect(() => {
    if (open && admin) {
      if (admin.permissions) {
        setPermissions({
          add: admin.permissions.add === 1 || admin.permissions.add === true,
          edit: admin.permissions.edit === 1 || admin.permissions.edit === true,
          delete:
            admin.permissions.delete === 1 || admin.permissions.delete === true,
          view: admin.permissions.view === 1 || admin.permissions.view === true,
          print:
            admin.permissions.print === 1 || admin.permissions.print === true,
          export:
            admin.permissions.export === 1 || admin.permissions.export === true,
        });
      } else {
        setPermissions({
          add: false,
          edit: false,
          delete: false,
          view: false,
          print: false,
          export: false,
        });
      }
      setError("");
      setSuccess(false);
    }
  }, [open, admin]);

  const handleCheckChange = (key) => {
    setPermissions((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
    setError("");
  };

  const handleSelectAll = () => {
    const allSelected = permissionsList.every((p) => permissions[p.key]);
    if (allSelected) {
      // فك التحديد عن الكل
      setPermissions({
        add: false,
        edit: false,
        delete: false,
        view: false,
        print: false,
        export: false,
      });
    } else {
      // تحديد الكل
      setPermissions({
        add: true,
        edit: true,
        delete: true,
        view: true,
        print: true,
        export: true,
      });
    }
  };

  const handleSubmit = async () => {
    if (!admin || !admin.id) {
      setError("لا يوجد حساب محدد لإضافة الصلاحيات");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess(false);

    try {
      // تحويل القيم إلى 1 و 0 للـ API
      const payload = {
        add: permissions.add ? 1 : 0,
        edit: permissions.edit ? 1 : 0,
        delete: permissions.delete ? 1 : 0,
        view: permissions.view ? 1 : 0,
        print: permissions.print ? 1 : 0,
        export: permissions.export ? 1 : 0,
      };

      const response = await manageAdminAccountPermissions(admin.id, payload);

      setSuccess(true);

      // إغلاق الديالوج بعد 1.5 ثانية
      setTimeout(() => {
        onUpdated && onUpdated(response);
        onClose();
      }, 1500);
    } catch (err) {
      console.error("خطأ في إضافة الصلاحيات:", err);
      setError(err.message || "حدث خطأ في إضافة الصلاحيات");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError("");
      setSuccess(false);
      onClose();
    }
  };

  if (!admin) return null;

  const allSelected = permissionsList.every((p) => permissions[p.key]);
  const someSelected = permissionsList.some((p) => permissions[p.key]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth dir="rtl">
      <DialogTitle
        sx={{
          fontWeight: "bold",
          textAlign: "right",
          display: "flex",
          alignItems: "center",
          gap: 1,
          backgroundColor: "info.light",
          color: "info.dark",
        }}
      >
        <SecurityIcon />
        إدارة صلاحيات: {admin.name}
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Success Alert */}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            تم حفظ الصلاحيات بنجاح
          </Alert>
        )}

        {/* Admin Info */}
        <Paper
          sx={{
            p: 2,
            mb: 3,
            backgroundColor: "#f5f5f5",
            border: "1px solid #ddd",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <SecurityIcon sx={{ fontSize: 40, color: "info.main" }} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                {admin.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                الدور: {admin.role === "admin" ? "مدير نظام" : admin.role}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                ID: {admin.id}
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Divider sx={{ my: 2 }}>
          <Typography variant="subtitle2" color="textSecondary">
            الصلاحيات المتاحة
          </Typography>
        </Divider>

        {/* Select All Checkbox */}
        <Paper
          sx={{
            p: 2,
            mb: 3,
            backgroundColor: "primary.light",
            border: "2px solid",
            borderColor: "primary.main",
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected && !allSelected}
                onChange={handleSelectAll}
                disabled={loading}
              />
            }
            label={
              <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                تحديد جميع الصلاحيات
              </Typography>
            }
            sx={{ mb: 0 }}
          />
        </Paper>

        {/* Permissions Checkboxes */}
        <Grid container spacing={2}>
          {permissionsList.map((perm) => (
            <Grid item xs={12} md={6} key={perm.key}>
              <Paper
                sx={{
                  p: 2,
                  border: "1px solid",
                  borderColor: permissions[perm.key]
                    ? "success.main"
                    : "divider",
                  backgroundColor: permissions[perm.key]
                    ? "success.light"
                    : "background.paper",
                  transition: "all 0.2s",
                  cursor: loading ? "not-allowed" : "pointer",
                  "&:hover": {
                    borderColor: loading ? "divider" : "success.main",
                    boxShadow: loading ? "none" : 2,
                  },
                }}
                onClick={() => !loading && handleCheckChange(perm.key)}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={permissions[perm.key]}
                      onChange={() => handleCheckChange(perm.key)}
                      disabled={loading}
                    />
                  }
                  label={
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="h6" sx={{ fontSize: "1.3rem" }}>
                          {perm.icon}
                        </Typography>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: "bold",
                            color: permissions[perm.key] ? "success.dark" : "text.primary",
                          }}
                        >
                          {perm.label}
                        </Typography>
                        {permissions[perm.key] && (
                          <CheckCircleIcon
                            sx={{
                              fontSize: 20,
                              color: "success.main",
                              ml: "auto",
                            }}
                          />
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {perm.description}
                      </Typography>
                    </Box>
                  }
                  sx={{ width: "100%", mr: 0 }}
                />
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Permissions Summary */}
        <Paper
          sx={{
            p: 2,
            mt: 3,
            backgroundColor: "#f0f7ff",
            border: "1px solid #b3d9ff",
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
            ملخص الصلاحيات المختارة:
          </Typography>
          <Box>
            {permissionsList.filter((p) => permissions[p.key]).length === 0 ? (
              <Typography variant="body2" color="error">
                لم يتم تحديد أي صلاحيات
              </Typography>
            ) : (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {permissionsList
                  .filter((p) => permissions[p.key])
                  .map((p) => (
                    <Box
                      key={p.key}
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        backgroundColor: "success.main",
                        color: "white",
                        borderRadius: 1,
                        fontSize: "0.875rem",
                        fontWeight: "bold",
                      }}
                    >
                      ✓ {p.label}
                    </Box>
                  ))}
              </Box>
            )}
          </Box>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
        <Button onClick={handleClose} disabled={loading} variant="outlined">
          إلغاء
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="info"
          disabled={loading}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
          startIcon={!loading && <SecurityIcon />}
        >
          {loading && <CircularProgress size={20} color="inherit" />}
          {loading ? "جاري الحفظ..." : "حفظ الصلاحيات"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddPermissionsDialog;
